import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { backendEndPointToken } from '../app.config';

@Injectable({
  providedIn: 'root'
})
export class TimeService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);

  // Offset in milliseconds: (ServerTime - LocalTime)
  private clockSkew = 0;
  private isSynced = new BehaviorSubject<boolean>(false);
  private static OriginalDate = window.Date;
  
  sync$ = this.isSynced.asObservable();

  constructor() {
    this.applyGlobalPatch();
    this.syncWithServer();
  }

  /**
   * Globally overrides window.Date to automatically account for server skew.
   * This ensures DatePipe, new Date(), and Date.now() are always "Correct".
   */
  private applyGlobalPatch() {
    const self = this;
    const OriginalDate = TimeService.OriginalDate;

    // 1. Create the Mock Date Constructor
    function MockDate(...args: any[]) {
      if (args.length === 0) {
        // If called as 'new Date()', return current time + skew
        return new OriginalDate(OriginalDate.now() + self.clockSkew);
      }
      // If called with arguments (e.g. 'new Date("2020-01-01")'), use original behavior
      return new (OriginalDate as any)(...args);
    }

    // 2. Mirror original properties and prototype
    MockDate.prototype = OriginalDate.prototype;
    
    // 3. Patch Static Methods
    MockDate.now = () => OriginalDate.now() + self.clockSkew;
    MockDate.UTC = OriginalDate.UTC;
    MockDate.parse = OriginalDate.parse;

    // 4. Global Injection
    window.Date = MockDate as any;

    console.log('[TimeService] Global Date patch applied.');
  }

  /**
   * Pings the server to get the 'Date' header and calculate skew
   */
  async syncWithServer() {
    try {
      const response = await firstValueFrom(
        this.http.get(`${this.backendUrl}/metadata`, { observe: 'response', responseType: 'text' })
      );

      const serverDateHeader = response.headers.get('Date');
      if (serverDateHeader) {
        // We use OriginalDate here to get the "Real" local time for comparison
        const serverTime = new TimeService.OriginalDate(serverDateHeader).getTime();
        const localTime = TimeService.OriginalDate.now();
        
        this.clockSkew = serverTime - localTime;
        this.isSynced.next(true);

        console.log(`[TimeService] Server Sync Successful. New Clock Skew: ${this.clockSkew}ms`);
      }
    } catch (error) {
      console.error('[TimeService] Failed to sync with server time', error);
    }
  }

  /**
   * Returns a Date object representing the "True UTC" time, 
   * adjusted for any PC clock error.
   */
  getTrueDate(): Date {
    return new Date(Date.now() + this.clockSkew);
  }

  /**
   * Formats a date for FHIR (YYYY-MM-DD) using the "True Date" 
   * logic to ensure search filters are accurate.
   */
  toFhirDate(date: Date): string {
    // If the date passed in is 'today', we adjust it by our skew
    const adjustedDate = new Date(date.getTime() + this.clockSkew);
    
    const year = adjustedDate.getFullYear();
    const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
    const day = String(adjustedDate.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
}
